import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { UserPlus, Trash, Warning, SignOut, ShieldCheck, Eye, EyeSlash } from '@phosphor-icons/react';

export default function Admin() {
  const { profile, adminListUsers, adminCreateCustomer, adminDeleteUser, signOut } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({ loginId: '', password: '', companyName: '' });

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    setLoading(true);
    const { data, error } = await adminListUsers();
    setLoading(false);
    if (error) {
      toast.error(error.message || 'Gagal memuat user');
      return;
    }
    setUsers(data || []);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.loginId.trim() || !form.password || !form.companyName.trim()) {
      toast.error('Lengkapi semua field');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }
    setCreating(true);
    const { error } = await adminCreateCustomer({
      loginId: form.loginId.trim(),
      password: form.password,
      companyName: form.companyName.trim(),
    });
    setCreating(false);
    if (error) {
      toast.error(error.message || 'Gagal membuat user');
      return;
    }
    toast.success(`User ${form.loginId.trim()} berhasil dibuat`);
    setForm({ loginId: '', password: '', companyName: '' });
    setShowAdd(false);
    refresh();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error } = await adminDeleteUser(deleteTarget.user_id);
    setDeleting(false);
    if (error) {
      toast.error(error.message || 'Gagal menghapus user');
      return;
    }
    toast.success(`User ${deleteTarget.login_id} dihapus beserta semua datanya`);
    setDeleteTarget(null);
    refresh();
  };

  const handleSignOut = async () => {
    await signOut();
    toast.success('Berhasil keluar');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-page">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-zinc-200 px-4 sm:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck size={28} weight="bold" className="text-red-600" />
          <div>
            <h1 className="text-lg sm:text-2xl font-black uppercase tracking-widest text-zinc-900" style={{ fontFamily: 'Chivo' }}>
              ADMIN PANEL
            </h1>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
              Login sebagai: <span className="text-zinc-900">{profile?.login_id || '—'}</span>
            </p>
          </div>
        </div>
        <button
          onClick={handleSignOut}
          className="flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-wider text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100 border border-zinc-300 rounded-lg transition-colors"
          data-testid="admin-signout"
        >
          <SignOut size={16} weight="bold" />
          Keluar
        </button>
      </header>

      <div className="p-4 sm:p-8">
        {/* Add user section */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-zinc-900" style={{ fontFamily: 'Chivo' }}>
            DAFTAR USER ({users.length})
          </h2>
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-xs tracking-wider py-3 px-6 flex items-center gap-2 rounded-lg shadow-sm transition-colors"
            data-testid="add-user-toggle"
          >
            <UserPlus size={16} weight="bold" />
            TAMBAH USER
          </button>
        </div>

        {showAdd && (
          <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-8 shadow-sm" data-testid="add-user-form">
            <h3 className="text-base font-black uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-2 mb-4" style={{ fontFamily: 'Chivo' }}>
              USER BARU
            </h3>
            <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">ID Perusahaan *</label>
                <input
                  type="text"
                  value={form.loginId}
                  onChange={(e) => setForm({ ...form, loginId: e.target.value })}
                  placeholder="contoh: GTB01"
                  className="w-full bg-white border border-zinc-300 rounded-lg p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                  required
                  data-testid="new-login-id"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Nama Perusahaan *</label>
                <input
                  type="text"
                  value={form.companyName}
                  onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                  placeholder="contoh: PT Andi Logistik"
                  className="w-full bg-white border border-zinc-300 rounded-lg p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                  required
                  data-testid="new-company-name"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Password Awal *</label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Min. 6 karakter"
                    className="w-full bg-white border border-zinc-300 rounded-lg p-3 pr-10 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                    required
                    data-testid="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-500 hover:text-zinc-900"
                    title={showPass ? 'Sembunyikan' : 'Tampilkan'}
                  >
                    {showPass ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                  </button>
                </div>
              </div>
              <div className="md:col-span-3 flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={creating}
                  className="bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-xs tracking-wider py-3 px-6 rounded-lg shadow-sm transition-colors disabled:opacity-50"
                  data-testid="submit-new-user"
                >
                  {creating ? 'MEMBUAT...' : 'BUAT USER'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowAdd(false); setForm({ loginId: '', password: '', companyName: '' }); }}
                  className="border border-zinc-300 hover:bg-zinc-100 text-zinc-900 font-mono uppercase text-xs tracking-wider py-3 px-6 rounded-lg transition-colors"
                >
                  BATAL
                </button>
              </div>
            </form>
          </div>
        )}

        {/* User list */}
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-8 text-center text-zinc-500 font-mono text-sm">Memuat...</div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-zinc-500 font-mono text-sm">Belum ada user terdaftar.</p>
              <p className="text-zinc-400 font-mono text-xs mt-1">Klik "TAMBAH USER" untuk membuat user customer pertama.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50">
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">ID Perusahaan</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Nama Perusahaan</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Armada</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Ban</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Dibuat</th>
                    <th className="text-right p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50" data-testid={`user-row-${u.login_id}`}>
                      <td className="p-4 text-zinc-900 font-mono text-sm font-bold">{u.login_id}</td>
                      <td className="p-4 text-zinc-700 font-mono text-sm">{u.company_name || '—'}</td>
                      <td className="p-4 text-zinc-700 font-mono text-sm">{u.truck_count}</td>
                      <td className="p-4 text-zinc-700 font-mono text-sm">{u.tire_count}</td>
                      <td className="p-4 text-zinc-500 font-mono text-xs">
                        {new Date(u.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => setDeleteTarget(u)}
                          className="inline-flex items-center gap-1 border border-red-300 hover:bg-red-50 text-red-600 font-mono uppercase text-[10px] tracking-wider py-2 px-3 rounded-lg transition-colors"
                          data-testid={`delete-user-${u.login_id}`}
                        >
                          <Trash size={12} weight="bold" />
                          HAPUS
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-[10px] font-mono text-zinc-400 mt-6 leading-relaxed">
          Catatan: Menghapus user akan menghapus permanen semua armada, ban, riwayat, dan data scrap perusahaan tersebut. ID Perusahaan yang sama dapat digunakan lagi setelah penghapusan.
        </p>
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="delete-confirm">
          <div className="bg-white border border-zinc-300 rounded-xl w-full max-w-md shadow-xl">
            <div className="p-6 border-b border-zinc-200 flex items-center gap-3">
              <Warning size={28} weight="bold" className="text-red-600" />
              <h2 className="text-base font-black uppercase tracking-wider text-zinc-900" style={{ fontFamily: 'Chivo' }}>
                HAPUS USER?
              </h2>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-sm font-mono text-zinc-700">
                Anda akan menghapus user <span className="font-bold text-zinc-900">{deleteTarget.login_id}</span>
                {deleteTarget.company_name ? <> dari perusahaan <span className="text-zinc-900">{deleteTarget.company_name}</span></> : null}.
              </p>
              <div className="bg-red-50 border border-red-300 rounded-lg p-3">
                <p className="text-xs font-mono text-red-800 leading-relaxed">
                  Akan dihapus permanen:<br/>
                  • {deleteTarget.truck_count} armada<br/>
                  • {deleteTarget.tire_count} ban<br/>
                  • Seluruh riwayat mount/dismount<br/>
                  • Seluruh data scrap & anomali
                </p>
              </div>
              <p className="text-[10px] font-mono text-zinc-500">Tindakan ini TIDAK DAPAT DIBATALKAN.</p>
            </div>
            <div className="p-6 border-t border-zinc-200 flex gap-3">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-xs tracking-wider py-3 rounded-lg shadow-sm transition-colors disabled:opacity-50"
                data-testid="confirm-delete-user"
              >
                {deleting ? 'MENGHAPUS...' : 'YA, HAPUS PERMANEN'}
              </button>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 border border-zinc-300 hover:bg-zinc-100 text-zinc-900 font-mono uppercase text-xs tracking-wider py-3 rounded-lg transition-colors"
              >
                BATAL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
